import React, { useEffect, useRef, useState } from 'react';
import { MapContainer } from '@components/organism/MapContainer';
import { Map } from '@components/atoms/Map';
import { CarSearchWithPicker } from '@components/organism/CarSearchWithPicker/CarSearchWithPicker';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import styles from './MapPage.module.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useData } from '@hooks/useData';
import { AvailabilityWithPicker } from '@components/organism/AvailabilityWithPicker/AvailabilityWithPicker';
import { Loader } from '@components/atoms/Loader';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { MobileContainer } from '@components/organism/MobileContainer';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { ActiveDriversTable } from '@components/molecules/ActiveDriversTable/ActiveDriversTable';
import { useGetAllDrivers } from '@services/useGetAllDrivers';
import { useGetActiveDrivers } from '@services/useGetActiveDrivers';
import profileStyles from '@components/molecules/UserArea/UserArea.module.css';
import { SITE_URLS } from '@constants';
import { useRoleChecker } from '@hooks/useRoleChecker';

export function MapPage() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { login, availableDrivers } = useData();
    const { isDriver } = useRoleChecker();
    const { drivers, request, loading, error } = useGetActiveDrivers();
    const [selectedView, setSelectedView] = useState<'map' | 'list' | null>(null);
    const { request: fetchAvailableDrivers, error: availabilityError, lastResponseTime } = useGetAllDrivers();
    const fetchAvailableRef = useRef(fetchAvailableDrivers);
    fetchAvailableRef.current = fetchAvailableDrivers;
    const initialRequestStarted = useRef(false);
    const showMap = selectedView === 'map';

    useEffect(() => {
        if (availableDrivers != null) {
            setSelectedView((current) => current ?? ((availableDrivers.data?.length ?? 0) > 0 ? 'map' : 'list'));
        } else if (login && !initialRequestStarted.current) {
            initialRequestStarted.current = true;
            fetchAvailableRef.current();
        }
    }, [login, availableDrivers]);

    useEffect(() => {
        if (login) request();
    }, [login, i18n.language, request]);

    useEffect(() => {
        if (!login) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    if (selectedView === null) {
        return (
            <MapContainer>
                <div className="d-flex align-items-center justify-content-center w-100 h-100">
                    {availabilityError || lastResponseTime ? (
                        <div role="alert"><ErrorMessages messages={[availabilityError || t('common.error-occured')]} /></div>
                    ) : (
                        <div role="status" aria-label={t('map-view.loading')}><Loader /></div>
                    )}
                </div>
            </MapContainer>
        );
    }

    return (
        <MapContainer>
            <div className="position-relative w-100 h-100 d-flex flex-column">
                {showMap && <Map locations={[]} style={JSON.stringify({ height: '100%', width: '100%' })} />}

                <div className={`${showMap ? 'position-absolute top-0' : 'position-relative'} z-3 p-4 d-flex align-items-center justify-content-between w-100`}>
                    <RoundIcon
                        classes={styles.roundIcon}
                        icon="fa-thin fa-bars"
                        variant={RoundIconVariants.variant1}
                        onClick={() => navigate('/my-profile')}
                    />
                    <ToggleSwitch
                        id="map-list-view"
                        checked={showMap}
                        onChange={(checked) => setSelectedView(checked ? 'map' : 'list')}
                        optionLabels={[t('map-view.map'), t('map-view.list')]}
                        optionIcons={['fa-thin fa-map', 'fa-thin fa-list']}
                        ariaLabel={t('map-view.toggle')}
                    />
                </div>

                {showMap ? (
                    <div className="position-absolute bottom-0 w-100 z-2">
                        {isDriver ? <AvailabilityWithPicker /> : <CarSearchWithPicker />}
                    </div>
                ) : (
                    <div className="flex-grow-1 overflow-auto">
                        <MobileContainer>
                            <Container classes={profileStyles.containerBackground}>
                                <div className="d-flex justify-content-center">
                                    <Typography tagVariant={TypographyTagVariants.h1} fontSize="24" fontWeight="800">
                                        {t('active-drivers.title')}
                                    </Typography>
                                </div>
                                <hr />
                                {loading ? (
                                    <div role="status" aria-label={t('active-drivers.loading')}><Loader /></div>
                                ) : error ? (
                                    <div role="alert"><ErrorMessages messages={[error]} /></div>
                                ) : (
                                    <ActiveDriversTable drivers={drivers} />
                                )}
                            </Container>
                        </MobileContainer>
                    </div>
                )}
            </div>
        </MapContainer>
    );
}
